import React, { useState, RefObject } from 'react';
import { Card, Tabs, Tab } from '@/components/ui';
import { Project } from '../../../types/Project';
import { Task } from '../../../types/Task';
import { RoutineTask } from '../../../types/RoutineTask';
import { ForemanArea } from '../../../types/ForemanArea';
import ProjectTasksTab from './ProjectTasksTab';
import OtherCostsContent from './OtherCostsContent';
import InvoiceTab from './InvoiceTab';
import VendorInvoicesTab from './VendorInvoicesTab';
import ServerPagination from '../../../components/ServerPagination';
import OtherDocumentsTab from './OtherDocumentsTab';
import ProjectPurchaseOrdersTab from './ProjectPurchaseOrdersTab';
import { usePermissions } from '../../../hooks/usePermissions';

interface TaskPagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface ProjectDetailsTabbedProps {
  project: Project;
  foremanAreas: ForemanArea[];
  onTaskSaved: (
    task: Task | RoutineTask,
    options?: { successToast?: string; isNew?: boolean; keepOpen?: boolean }
  ) => void | Promise<void>;
  onTaskAction?: (action: string, task: Task) => void;
  onProjectUpdate: (updatedProject: Project) => void;
  taskPagination: TaskPagination;
  onTaskPageChange: (page: number) => void;
  onTaskPageSizeChange: (pageSize: number) => void;
  loading: boolean;
  tasksListRef?: RefObject<HTMLDivElement>;
}

const ProjectDetailsTabbed: React.FC<ProjectDetailsTabbedProps> = ({
  project,
  foremanAreas,
  onTaskSaved,
  onTaskAction,
  onProjectUpdate,
  taskPagination,
  onTaskPageChange,
  onTaskPageSizeChange,
  loading,
  tasksListRef
}) => {
  const { hasPermission } = usePermissions();
  const canViewPurchaseOrders = hasPermission('po:view');
  const [activeTab, setActiveTab] = useState<
    'tasks' | 'other-costs' | 'purchase-orders' | 'mott-invoices' | 'vendor-invoices' | 'other-documents'
  >('tasks');

  return (
    <Card>
      <Card.Body>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k as typeof activeTab)}
        >
          <Tab
            eventKey="tasks"
            title={<><i className="bi bi-list-task tw-mr-2"></i>Tasks</>}
          >
            <ProjectTasksTab
              project={project}
              foremanAreas={foremanAreas}
              onTaskSaved={onTaskSaved}
              onTaskAction={onTaskAction}
              listContainerRef={tasksListRef}
            />

            {/* Task Pagination */}
            {taskPagination && taskPagination.totalPages > 1 && (
              <ServerPagination
                currentPage={taskPagination.page}
                totalPages={taskPagination.totalPages}
                totalCount={taskPagination.totalCount}
                pageSize={taskPagination.pageSize}
                onPageChange={onTaskPageChange}
                onPageSizeChange={onTaskPageSizeChange}
                loading={loading}
                className="tw-mt-4"
              />
            )}
          </Tab>
          <Tab
            eventKey="other-costs"
            title={<><i className="bi bi-currency-dollar tw-mr-2"></i>Other Costs</>}
          >
            <OtherCostsContent
              project={project}
              onUpdate={onProjectUpdate}
            />
          </Tab>
          {canViewPurchaseOrders && (
            <Tab
              eventKey="purchase-orders"
              title={<><i className="bi bi-cart tw-mr-2"></i>Purchase Orders</>}
            >
              <ProjectPurchaseOrdersTab projectId={project.id} />
            </Tab>
          )}
          <Tab
            eventKey="mott-invoices"
            title={<><i className="bi bi-receipt tw-mr-2"></i>MoTT invoices</>}
          >
            <InvoiceTab projectId={project.id} />
          </Tab>
          <Tab
            eventKey="vendor-invoices"
            title={<><i className="bi bi-receipt tw-mr-2"></i>Vendor Invoices</>}
          >
            <VendorInvoicesTab projectId={project.id} />
          </Tab>
          <Tab
            eventKey="other-documents"
            title={<><i className="bi bi-file-earmark-text tw-mr-2"></i>Other documents</>}
          >
            <OtherDocumentsTab projectId={project.id} />
          </Tab>
        </Tabs>
      </Card.Body>
    </Card>
  );
};

export default ProjectDetailsTabbed; 